import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

interface Profile {
  id: string;
  telegram_id: number | null;
  first_name: string;
  last_name: string | null;
  username: string | null;
  avatar_url: string | null;
  phone: string | null;
  created_at: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer profile fetch with setTimeout
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 0);
        } else {
          setProfile(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching profile:', error);
        return;
      }

      if (!data) {
        // Profile doesn't exist yet — create a minimal one
        const { data: userData } = await supabase.auth.getUser();
        const meta = userData?.user?.user_metadata;
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .upsert({
            id: userId,
            first_name: meta?.first_name || meta?.name || '',
            telegram_id: meta?.telegram_id || 0,
            avatar_url: meta?.photo_url || null,
          }, { onConflict: 'id' })
          .select('*')
          .maybeSingle();

        if (insertError) {
          logger.error('Error creating profile:', insertError);
          return;
        }
        setProfile(newProfile);

        // Apply deferred referral code from registration metadata
        if (meta?.referral_code) {
          logger.debug('[Auth] New profile created, applying referral code:', meta.referral_code);
          try {
            const { applyReferralCodeForUser } = await import('@/components/auth/EmailRegisterForm');
            await applyReferralCodeForUser(userId, meta.referral_code);
            logger.debug('[Auth] Referral code applied successfully:', meta.referral_code);
          } catch (err) {
            logger.error('[Auth] Error applying deferred referral code:', err);
          }
        } else {
          logger.debug('[Auth] New profile created, no referral code in metadata');
        }
      } else {
        setProfile(data);

        // Check if deferred referral code needs to be applied (e.g. after email confirmation)
        const { data: userData } = await supabase.auth.getUser();
        const meta = userData?.user?.user_metadata;
        logger.debug('[Auth] Existing profile found. user_metadata:', JSON.stringify(meta));
        if (meta?.referral_code) {
          logger.debug('[Auth] Found referral code in metadata:', meta.referral_code, '- attempting to apply');
          try {
            const { applyReferralCodeForUser } = await import('@/components/auth/EmailRegisterForm');
            await applyReferralCodeForUser(userId, meta.referral_code);
            logger.debug('[Auth] Deferred referral code applied:', meta.referral_code);
          } catch (err) {
            logger.error('[Auth] Error applying deferred referral code:', err);
          }
        }
      }
    } catch (error) {
      logger.error('Error fetching profile:', error);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
